import UserLayout from '../Layout/UserLayout'
import Grid from '@mui/material/Grid'
import CardNavigationCenter from './components/CardNavigationCenter'
import axios from 'axios'
import Button from '@mui/material/Button'
import { useEffect, useState, useRef } from 'react'
import { MenuItem, TextField } from '@mui/material'
import ReportDownloadView from "./ReportDownloadView";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from 'file-saver';
import ApexCharts from 'apexcharts';

// 👇 IMPORTANT: import your chart components
import StrongestComponent from './components/charts/StrongestComponent';
import WeekestComponent from './components/charts/WeekestComponent';
import PieComponent from './components/charts/PieComponent';

export default function Report() {

    const authUserObj = localStorage.getItem('authUser');
    const authUser = JSON.parse(authUserObj);

    const [sessions, setSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState('');
    const [selfEvaluations, setSelfEvaluations] = useState([]);
    const [currentUserId, setCurrentUserId] = useState('');

    const [isLoading, setIsLoading] = useState(false);

    const [executiveSummary, setExecutiveSummary] = useState({});
    const [domainSummary, setDomainSummary] = useState([]);
    const [highLowScore, setHighLowScore] = useState([]);
    const [positiveNegativeGap, setPositiveNegativeGap] = useState([]);
    const [detailedResults, setDetailedResults] = useState([]);

    const [strongestImageUrl, setStrongestImageUrl] = useState(null);
    const [weakestImageUrl, setWeakestImageUrl] = useState(null);

    const [generateCharts, setGenerateCharts] = useState(false);
    const [chartsReady, setChartsReady] = useState(0);
    const chartsReadyRef = useRef(0);

    const chartImagesRef = useRef({
        strongest: null,
        weakest: null,
        domains: []
    });

    // ================= CHART CAPTURE =================

    const strongestCaptureChart = async (chartInstance) => {
        const imageUrl = await chartInstance.dataURI();
        chartImagesRef.current.strongest = imageUrl.imgURI;
    
        chartsReadyRef.current += 1;
    };

    const weakestCaptureChart = async (chartInstance) => {
        const imageUrl = await chartInstance.dataURI();
        chartImagesRef.current.weakest = imageUrl.imgURI;
    
        chartsReadyRef.current += 1;
    };

    const domainCaptureChart = async (chartId, index) => {
        try {
            const imageUrl = await ApexCharts.exec(chartId, 'dataURI', {
                scale: 2 // quality + color
            });
    
            // ✅ store in ref (NOT state)
            chartImagesRef.current.domains[index] = imageUrl.imgURI;
    
            // ✅ increment counter
            chartsReadyRef.current += 1;
    
        } catch (error) {
            console.error('Chart capture error:', error);
        }
    };

    // ================= API =================

    useEffect(() => {
        getSessions();
    }, [])

    const getSessions = async () => {
        const res = await axios.get(`/api/sessions?status=0`);
        if (res.data.success) setSessions(res.data.data);
    };

    const handleSessionChange = async (sessionId) => {
        setCurrentUserId('');
        setCurrentSessionId(sessionId);
        await getSessionsUsers(sessionId);
    };

    const getSessionsUsers = async (sessionId) => {
        const res = await axios.get(`/api/self_evaluations?sessionId=${sessionId}`);
        if (res.data.success) setSelfEvaluations(res.data.data);
    };

    const handleUserChange = (userId) => {
        setCurrentUserId(userId);
    };

    // ================= DATA FETCH =================

    useEffect(() => {
        if (currentSessionId && currentUserId) {
            setIsLoading(true);
            getExecutiveSummaryList();
            getDomainSummary();
            getHighandLowScoreList();
            getPositiveandNegativeGapList();
            getDetailedResults();
        }
    }, [currentSessionId, currentUserId]);

    const getExecutiveSummaryList = async () => {
        const res = await axios.get(`/api/excutive_summary?session_id=${currentSessionId}&userId=${currentUserId}`);
        if (res.data.success) setExecutiveSummary(res.data.data);
    };

    const getDomainSummary = async () => {
        const res = await axios.get(`/api/summary_domain?session_id=${currentSessionId}&userId=${currentUserId}`);
        if (res.data.success) setDomainSummary(res.data.data);
    };

    const getHighandLowScoreList = async () => {
        const res = await axios.get(`/api/high_low_score?session_id=${currentSessionId}&userId=${currentUserId}`);
        if (res.data.success) setHighLowScore(res.data.data);
    };

    const getPositiveandNegativeGapList = async () => {
        const res = await axios.get(`/api/possitive_negative_gap?session_id=${currentSessionId}&userId=${currentUserId}`);
        if (res.data.success) setPositiveNegativeGap(res.data.data);
    };

    const getDetailedResults = async () => {
        const res = await axios.get(`/api/detailed_result?session_id=${currentSessionId}&userId=${currentUserId}`);
        if (res.data.success) {
            setDetailedResults(res.data.data);
            setIsLoading(false);
        }
    };

    // ================= DOWNLOAD =================

    const handleDownload = async () => {
        try {
            chartsReadyRef.current = 0;
            chartImagesRef.current = {
                strongest: null,
                weakest: null,
                domains: []
            };
    
            setGenerateCharts(true);
    
            const totalCharts = 2 + domainSummary.length;
    
            // wait for charts render
            await new Promise(resolve => setTimeout(resolve, 500));
    
            // wait for capture
            await new Promise(resolve => {
                const interval = setInterval(() => {
                    if (chartsReadyRef.current >= totalCharts) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 100);
    
                setTimeout(() => {
                    clearInterval(interval);
                    resolve();
                }, 5000);
            });
    
            const res = await axios.get(`/api/getReport?userId=${currentUserId}&sessionId=${currentSessionId}`);
    
            if (res.data.success) {
    
                // ✅ DIRECT use ref data (NO STATE DELAY)
                const reportData = {
                    session_name: res.data.data.session.name,
                    user_name: res.data.data.user.name,
                    executive_summary: executiveSummary,
                    domain_summary: domainSummary.map((d, i) => ({
                        ...d,
                        chart_img: chartImagesRef.current.domains[i]
                    })),
                    high_low_score: highLowScore,
                    positive_negative_gap: positiveNegativeGap,
                    detailed_results: detailedResults,
                    strongestImageUrl: chartImagesRef.current.strongest,
                    weakestImageUrl: chartImagesRef.current.weakest
                };
    
                const blob = await pdf(
                    <ReportDownloadView report={reportData} />
                ).toBlob();
    
                saveAs(blob, res.data.data.user.name + ".pdf");
            }
    
            setGenerateCharts(false);
    
        } catch (err) {
            console.error("Error:", err);
        }
    };

    // ================= UI =================

    return (
        <UserLayout>

            <Grid container spacing={6}>
                <Grid item xs={12} md={2}>
                    <h1>Reports</h1>
                </Grid>

                <Grid item xs={12} md={3}>
                    <TextField select fullWidth size='small'
                        label='Session'
                        value={currentSessionId}
                        onChange={(e) => handleSessionChange(e.target.value)}
                    >
                        {sessions.map(s => (
                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={12} md={3}>
                    <TextField select fullWidth size='small'
                        label='User'
                        value={currentUserId}
                        onChange={(e) => handleUserChange(e.target.value)}
                    >
                        {selfEvaluations.map(u => (
                            <MenuItem key={u.id} value={u.user.id}>{u.user.name}</MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {currentUserId && (
                    <Grid item xs={12} md={3}>
                        <Button variant='contained' onClick={handleDownload}>
                            Download Report
                        </Button>
                    </Grid>
                )}
            </Grid>

            { sessions && sessions.length > 0 ?
                currentUserId > 0 ?  
                <Grid container spacing={6}>            
                    <Grid item xs={12} md={12}>
                        <CardNavigationCenter 
                            isLoading={isLoading} 
                            executiveSummary={executiveSummary}
                            domainSummary={domainSummary}
                            highLowScore={highLowScore}
                            positiveNegativeGap={positiveNegativeGap}
                            detailedResults={detailedResults}
                            strongestCaptureChart={strongestCaptureChart}
                            weakestCaptureChart={weakestCaptureChart}
                            domainCaptureChart={domainCaptureChart}
                        />
                    </Grid>   
                </Grid>                 
                    
                : 
                    <Grid container spacing={6} justifyContent="center">            
                        <Grid item xs={12} md={6}>
                            <Grid sx={{ padding: 5}}><h6 className='mt-5 fw-normal'>Select session and its user to see the report.</h6></Grid>
                        </Grid>       
                    </Grid>
                :
                    <Grid container spacing={6} justifyContent="center">            
                        <Grid item xs={12} md={6}>
                            <Grid sx={{ padding: 5 }}><h6 className='mt-5 fw-normal'>No report available.</h6></Grid>
                        </Grid>       
                    </Grid>      
            }

            {/* ================= HIDDEN CHARTS ================= */}
            {generateCharts && (
                <div style={{ position: 'absolute', top: '-9999px', opacity: 0 }}>

                    {executiveSummary?.strongest_domain && (
                        <StrongestComponent
                            domain={executiveSummary.strongest_domain}
                            strongestCaptureChart={strongestCaptureChart}
                        />
                    )}

                    {executiveSummary?.weakest_domain && (
                        <WeekestComponent
                            domain={executiveSummary.weakest_domain}
                            weakestCaptureChart={weakestCaptureChart}
                        />
                    )}

                    {domainSummary.map((item, index) => (
                        <PieComponent
                            key={index}
                            chartData={item}
                            index={index}
                            domainCaptureChart={domainCaptureChart}
                        />
                    ))}

                </div>
            )}

        </UserLayout>
    );
}